import React, { createContext, useCallback, useContext, useState } from "react";
import { 
  getClientsPaginated,
  getClients, 
  getClientById, 
  createClient, 
  updateClient, 
  deleteClient, 
  ClientApiResponse
} from "../../services/clientService";
import { getErrorMessage } from "../../locales/translationUtils";
import LoadingOverlay from "../../components/Loading";
import Client from "../../types/client";
import Paginacao from "../../types/paginacao";
import { ApiResponse } from "../../services/api";

interface ClientContextProps {
  apiResponse: ApiResponse<Client> | null;
  clients: Client[] | null;
  clientById: Client | null;
  loading: boolean;
  error: string | null;
  fetchClient: (paginacao: Paginacao | null) => void;
  fetchClientById: (id: number) => Promise<Client | null>;
  postClient: (client: Client) => Promise<ClientApiResponse | null>;
  putClient: (id: number, client: Client) => Promise<ClientApiResponse | null>;
  excluiClient: (id: number) => Promise<boolean>;
}

interface ClientProviderProps {
  children: React.ReactNode;
}

const ClientContext = createContext<ClientContextProps | null>(null);

const ClientProvider: React.FC<ClientProviderProps> = ({ children }) => {
  const [apiResponse, setApiResponse] = useState<ApiResponse<Client> | null>(null);
  const [clients, setClient] = useState<Client[] | null>(null);
  const [clientById, setClientById] = useState<Client | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchClient = useCallback(async (paginacao: Paginacao | null) => {
    setError(null);
    try {
      setLoading(true);
      const response = (paginacao) ? await getClientsPaginated(paginacao) : await getClients();
      setApiResponse(response);
      setClient(response.data.values || []);
    } catch (err) {
      setClient([]);
      setError(getErrorMessage("fetchClientError"));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClientById = useCallback(async (id: number) => {
    setError(null);
    try {
      setLoading(true);
      const response = await getClientById(id);
      
      setClientById(response.data);
      return response.data;
    } catch (err) {
      setError(getErrorMessage("fetchClientByIdError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const postClient = useCallback(async (client: Client) => {
    setError(null);
    try {
      setLoading(true);
      const response = await createClient(client);
      return response;
    } catch (err) {
      setError(getErrorMessage("createClientError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const putClient = useCallback(async (id: number, client: Client) => {
    setError(null);
    try {
      setLoading(true);
      const response = await updateClient(id, client);
      return response;
    } catch (err) {
      setError(getErrorMessage("updateClientError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const excluiClient = useCallback(async (id: number) => {
    setError(null);
    try {
      setLoading(true);
      await deleteClient(id);
      return true;
    } catch (err) {
      setError(getErrorMessage("deleteClientError"));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <ClientContext.Provider 
      value={{ 
        apiResponse,
        clients, 
        clientById, 
        loading, 
        error, 
        fetchClient, 
        fetchClientById, 
        postClient, 
        putClient, 
        excluiClient 
      }}
    >
      <LoadingOverlay isLoading={loading} />
      {children}
    </ClientContext.Provider>
  );
};

export default ClientProvider;

export const useClient = (): ClientContextProps => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error(
      getErrorMessage("wrongUseProviderError", {
        auth: useClient?.name,
        providerName: ClientProvider?.name,
      })
    );
  }
  return context;
};
